function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import PlunkDropDown from "../../components/plunk-dropdown";
import DataShell, { Btn } from "../manage-data/shell";
import SiteAudienceCard from "../manage-data/site-audience";
import { fetchSales, titleCase, cleanTitle } from "../sales/territory-common";
import { RowMenu, DlCard, DlFoot, DlPager, fmtInt } from "./dl-kit";
import { decodeEntities } from "../stores/common";

/*
 * Manage Data › Products on the kit (dataflow Products mockup, 2026-10-09).
 *
 *   Site audience card (manage-data/site-audience.jsx), then the Catalog: Product (thumbnail, "SKU · Title"),
 *   Type, Feed price, Avg price sold, Availability, "···".
 *
 * Rows are the server page from GET /v1/product (ProductViewComponent). Search and the type filter
 * navigate to /Product with the same $where/$params query the global search uses, so paging keeps them.
 * Avg price sold comes from GET /v1/sales/explorer/products/all_time (500 a page, every page, pages
 * fetched together), mapped by product id / SKU; it fails soft (the column shows nothing). Only the
 * first 500 used to load, so any product outside the top 500 sellers showed a blank.
 * Feed types can arrive HTML-escaped ("Furniture &gt; Sofas"): decoded for display, and the type
 * filter matches the escaped and plain spellings.
 * When most rows on the page carry the same feed price, that price is a feed placeholder: it is marked
 * and a footer note explains that Plunk prices from what each product actually sold for.
 */

const N = v => Number(v) || 0;
const money = v => "$" + Math.round(N(v)).toLocaleString("en-US");
function displayTitle(t) {
  const s = cleanTitle(t);
  if (!s) return "";
  return /[A-Z]/.test(s) && s === s.toUpperCase() ? titleCase(s) : s;
}
function typeLeaf(t) {
  if (!t) return "";
  t = decodeEntities(t);
  const parts = String(t).split(">").map(x => x.trim()).filter(Boolean);
  return displayTitle(parts.length ? parts[parts.length - 1] : t);
}
function availabilityLabel(v) {
  if (!v) return "";
  const s = String(v).replace(/_/g, " ").trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function thumbSrc(link) {
  if (!link) return null;
  return String(link).split("?")[0] + "?width=72";
}
function urlParam(k) {
  try {
    return new URLSearchParams(window.location.search).get(k) || "";
  } catch (e) {
    return "";
  }
}

/** The placeholder price when at least 60% of the priced rows (3 or more) share one price; else null. */
function placeholderPrice(rows) {
  const priced = (rows || []).map(r => N(r.price)).filter(p => p > 0);
  if (priced.length < 3) return null;
  const counts = {};
  priced.forEach(p => {
    counts[p] = (counts[p] || 0) + 1;
  });
  let best = null,
    bestN = 0;
  Object.keys(counts).forEach(k => {
    if (counts[k] > bestN) {
      best = Number(k);
      bestN = counts[k];
    }
  });
  return bestN / priced.length >= 0.6 ? best : null;
}
class Thumb extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      broken: false
    });
  }
  render() {
    const src = thumbSrc(this.props.src);
    if (!src || this.state.broken) return /*#__PURE__*/React.createElement("span", {
      className: "dl-thumb",
      "aria-hidden": "true"
    });
    return /*#__PURE__*/React.createElement("span", {
      className: "dl-thumb"
    }, /*#__PURE__*/React.createElement("img", {
      src: src,
      alt: "",
      loading: "lazy",
      onError: () => this.setState({
        broken: true
      })
    }));
  }
}
export default class ProductsKit extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onSearchKey", e => {
      if (e.key === "Enter") this.applyFilters(this.state.q, this.state.type);
      if (e.key === "Escape" && this.state.q) {
        this.setState({
          q: ""
        });
        this.applyFilters("", this.state.type);
      }
    });
    this.state = {
      q: urlParam("q"),
      type: urlParam("type"),
      sold: null,
      soldComplete: false,
      cats: []
    };
    this._alive = true;
  }
  componentDidMount() {
    this.loadSold();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async loadSold() {
    try {
      const url = skip => `/sales/explorer/products/all_time?top=500&skip=${skip}&sort=revenue&dir=desc`;
      const res = await fetchSales(url(0));
      let rows = res && Array.isArray(res.rows) ? res.rows : [];
      const total = N(res && res.total) || rows.length;
      const skips = [];
      for (let s = 500; s < total && s < 20000; s += 500) skips.push(s);
      let missed = false;
      const more = await Promise.all(skips.map(s => fetchSales(url(s)).then(r => r && Array.isArray(r.rows) ? r.rows : []).catch(() => {
        missed = true;
        return [];
      })));
      more.forEach(m => {
        rows = rows.concat(m);
      });
      const sold = {};
      const cats = {};
      rows.forEach(r => {
        const avg = r.avgPrice != null ? N(r.avgPrice) : N(r.units) ? N(r.revenue) / N(r.units) : null;
        if (avg != null && avg > 0) {
          if (r.productId != null) sold["id:" + String(r.productId).toLowerCase()] = avg;
          if (r.sku) sold["sku:" + String(r.sku).toLowerCase()] = avg;
        }
        const c = typeLeaf(r.category);
        if (c) cats[c] = true;
      });
      if (this._alive) this.setState({
        sold,
        soldComplete: !missed && total <= rows.length,
        cats: Object.keys(cats)
      });
    } catch (e) {
      if (this._alive) this.setState({
        sold: {}
      });
    }
  }
  soldFor(r) {
    const sold = this.state.sold;
    if (!sold) return undefined;
    const a = sold["id:" + String(r.id || "").toLowerCase()];
    if (a != null) return a;
    const sku = r.sku || r.SKU;
    const b = sku ? sold["sku:" + String(sku).toLowerCase()] : undefined;
    return b != null ? b : null;
  }

  // Same OData shape as the global product search (search/registry.js): in-memory dynamic LINQ on /v1/product.
  applyFilters(q, type) {
    const term = String(q || "").trim().toLowerCase();
    const t = String(type || "").trim().toLowerCase();
    // the feed may store the type HTML-escaped; match both spellings
    const tEsc = t.replace(/&/g, "&amp;").replace(/>/g, "&gt;").replace(/</g, "&lt;");
    const kv = [];
    const searchExpr = "Title!=null&&Title.ToLower().Contains(@0)||Id.ToLower().Contains(@0)";
    let where = "",
      params = "";
    if (term && t) {
      where = `(${searchExpr})&&ProductType!=null&&(ProductType.ToLower().Contains(@1)||ProductType.ToLower().Contains(@2))`;
      params = `${term}|${t}|${tEsc}`;
    } else if (term) {
      where = searchExpr;
      params = term;
    } else if (t) {
      where = "ProductType!=null&&(ProductType.ToLower().Contains(@0)||ProductType.ToLower().Contains(@1))";
      params = `${t}|${tEsc}`;
    }
    if (q && String(q).trim()) kv.push("q=" + encodeURIComponent(String(q).trim()));
    if (type) kv.push("type=" + encodeURIComponent(type));
    if (where) kv.push("$where=" + encodeURIComponent(where), "$params=" + encodeURIComponent(params));
    this.props.gotoPage("/Product" + (kv.length ? "?" + kv.join("&") : ""));
  }
  render() {
    const {
      data,
      pagination,
      onUpdate,
      gotoPage
    } = this.props;
    const {
      q,
      type,
      sold,
      soldComplete,
      cats
    } = this.state;
    const rows = Array.isArray(data) ? data : [];
    const placeholder = placeholderPrice(rows);
    const pageTypes = rows.map(r => typeLeaf(r.productType)).filter(Boolean);
    const types = Array.from(new Set([].concat(cats || [], pageTypes, type ? [type] : []))).sort((a, b) => a.localeCompare(b));
    const total = pagination && pagination.count != null ? N(pagination.count) : rows.length;
    const filtered = !!(urlParam("q") || urlParam("type"));
    const controls = /*#__PURE__*/React.createElement("span", {
      className: "dl-controls"
    }, /*#__PURE__*/React.createElement("input", {
      className: "dl-search",
      type: "search",
      value: q,
      placeholder: "Search by name or SKU",
      onChange: e => this.setState({
        q: e.target.value
      }),
      onKeyDown: this.onSearchKey,
      title: "Press Enter to search"
    }), types.length ? /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "190px",
      value: type,
      onChange: v => {
        this.setState({
          type: v
        });
        this.applyFilters(q, v);
      },
      options: [["", "All types"]].concat(types.map(t => [t, t]))
    }) : null);
    return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Product",
      subtitle: "Where your data comes from and how Plunk uses it",
      actions: /*#__PURE__*/React.createElement(Btn, {
        primary: true,
        onClick: onUpdate
      }, "Update products")
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-grid dl-view"
    }, /*#__PURE__*/React.createElement(SiteAudienceCard, null), /*#__PURE__*/React.createElement(DlCard, {
      title: "Catalog",
      hint: `${fmtInt(total)} ${filtered ? "matching products" : "products from your product feed"}`,
      right: controls
    }, rows.length ? /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table dl-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", null, "Type"), /*#__PURE__*/React.createElement("th", {
      className: "num"
    }, "Feed price"), /*#__PURE__*/React.createElement("th", {
      className: "num"
    }, "Avg price sold"), /*#__PURE__*/React.createElement("th", null, "Availability"), /*#__PURE__*/React.createElement("th", {
      className: "dl-act",
      "aria-label": "Actions"
    }))), /*#__PURE__*/React.createElement("tbody", null, rows.map(r => {
      const sku = r.sku || r.SKU || r.id;
      const title = displayTitle(r.title);
      const name = [sku, title && title !== sku ? title : null].filter(Boolean).join(" · ");
      const price = N(r.price);
      const isPh = placeholder != null && price === placeholder;
      const avg = this.soldFor(r);
      const detail = `/Products/this_year/product/${encodeURIComponent(String(r.id))}`;
      return /*#__PURE__*/React.createElement("tr", {
        key: String(r.id)
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "dl-prod"
      }, /*#__PURE__*/React.createElement(Thumb, {
        src: r.imageLink
      }), /*#__PURE__*/React.createElement("a", {
        className: "dl-name",
        onClick: () => gotoPage(detail),
        title: cleanTitle(r.title) || ""
      }, name || String(r.id)))), /*#__PURE__*/React.createElement("td", null, typeLeaf(r.productType) || /*#__PURE__*/React.createElement("span", {
        className: "dl-muted"
      }, "Not set")), /*#__PURE__*/React.createElement("td", {
        className: `num${isPh ? " dl-amber" : ""}`
      }, price > 0 ? money(price) : /*#__PURE__*/React.createElement("span", {
        className: "dl-muted"
      }, "None"), isPh ? /*#__PURE__*/React.createElement("span", {
        className: "dl-ph"
      }, "placeholder") : null), /*#__PURE__*/React.createElement("td", {
        className: "num"
      }, avg ? money(avg) : sold && avg === null && soldComplete ? /*#__PURE__*/React.createElement("span", {
        className: "dl-muted"
      }, "Not sold") : null), /*#__PURE__*/React.createElement("td", null, availabilityLabel(r.availability)), /*#__PURE__*/React.createElement("td", {
        className: "dl-act"
      }, /*#__PURE__*/React.createElement(RowMenu, {
        label: name || String(r.id),
        items: [{
          label: "Open product",
          onClick: () => gotoPage(detail)
        }, r.link && {
          label: "View on your site",
          onClick: () => window.open(r.link, "_blank", "noopener")
        }, {
          label: "Update products",
          onClick: onUpdate
        }]
      })));
    })))) : /*#__PURE__*/React.createElement("p", {
      className: "dl-empty"
    }, filtered ? "No products match. Try another search or type." : "No products yet. Use Update products to load your product feed."), placeholder != null || pagination && N(pagination.pageCount) > 1 ? /*#__PURE__*/React.createElement(DlFoot, {
      tone: placeholder != null ? "amber" : null,
      right: /*#__PURE__*/React.createElement(DlPager, {
        pagination: pagination,
        onGo: gotoPage,
        noun: "products"
      })
    }, placeholder != null ? `Your product feed sends the same price (${money(placeholder)}) for every product, so Plunk uses the average price each product actually sold for. Fix the feed, or upload corrected prices with Update products.` : null) : null)));
  }
}